// canvas/pixel-cards.js: card art data and geometry for the cream restyle.
//
// The suit maps, portraits, portrait colours, pip layouts and the size
// table GEO are copied unchanged from the chosen cream mockup
// (docs/design-2026-10/restyle-cream/shared/cards.js, our own art drawn
// for this project). Colours come from the mockup's shared/cream.css.
//
// No canvas calls live here, so Node tests can import the data directly.

// ---- Inks (cream.css) ----

export const INK = '#261a22';          // outline, eyes, card edge
export const FACE = '#fcf5e3';         // card face
export const GOLD = '#e8ad45';         // trump frame, crowns
export const PORTRAIT_GROUND = '#f1e3c3';
export const PORTRAIT_LINE = '#dcc697';

export const SUIT_INK = {
  H: '#721026',
  D: '#ac420c',
  S: '#22305a',
  C: '#1d5a3f',
};

// Face-down back (cream.css --back-a/b/c): brick field, darker brick
// checks, a cream frame and a cream pixel sun.
export const BACK = {
  field: '#b5452e',
  check: '#94351f',
  frame: '#f6ead0',
};

// ---- Suits: 9x9 maps, 'X' filled (mockup SUIT_MAPS) ----

export const SUIT_COLS = 9;
export const SUIT_ROWS = 9;

export const SUIT_MAPS = {
  H: ['.XX...XX.', 'XXXX.XXXX', 'XXXXXXXXX', 'XXXXXXXXX', 'XXXXXXXXX', '.XXXXXXX.', '..XXXXX..', '...XXX...', '....X....'],
  D: ['....X....', '...XXX...', '..XXXXX..', '.XXXXXXX.', 'XXXXXXXXX', '.XXXXXXX.', '..XXXXX..', '...XXX...', '....X....'],
  S: ['....X....', '...XXX...', '..XXXXX..', '.XXXXXXX.', 'XXXXXXXXX', 'XXXXXXXXX', '.XX.X.XX.', '....X....', '..XXXXX..'],
  C: ['...XXX...', '..XXXXX..', '...XXX...', 'XX.XXX.XX', 'XXXXXXXXX', 'XXXXXXXXX', '.X..X..X.', '....X....', '..XXXXX..'],
};

// ---- Portraits: 16 x 22 cells (mockup PORTRAITS) ----
// o outline, s skin, k skin shade, h hair, g gold, c suit colour,
// w collar cream, e eye, m mouth, b beard.

export const PORTRAIT_COLS = 16;
export const PORTRAIT_ROWS = 22;

export const PORTRAITS = {
  J: [
    '...........ww...',
    '..........wwww..',
    '.........wwwwo..',
    '....oooooowwo...',
    '...occcccccco...',
    '..occccccccco...',
    '.oooooooooooooo.',
    '...ohhssssshho..',
    '...ohssssssso...',
    '...osesssseso...',
    '...osssskssso...',
    '...ossmmmmsso...',
    '....osssssso....',
    '.....oooooo.....',
    '....owwwwwwo....',
    '..occcwwwwccco..',
    '.occccwwwwcccco.',
    'occcccowwoccccco',
    'occgccccccccgcco',
    'occgccccccccgcco',
    'occcccccccccccco',
    'oooooooooooooooo',
  ],
  Q: [
    '.......gg.......',
    '...g...gg...g...',
    '...gg.gggg.gg...',
    '...gggggggggg...',
    '..ohhhhhhhhhho..',
    '.ohhhhhhhhhhhho.',
    '.ohhsssssssshho.',
    '.ohssssssssssho.',
    '.ohsessssssesho.',
    '.ohssssksssshho.',
    '.ohsssmmmsssho..',
    '.ohhsssssssshho.',
    '.ohhhosssssohhho',
    '.ohhh.ooooo.hhho',
    '.ohhhwwwwwwwhhho',
    '.ohhcwwwwwwwchho',
    '.occccwwwwwcccco',
    'occccccgccccccco',
    'occcccgggcccccco',
    'occccccgccccccco',
    'occcccccccccccco',
    'oooooooooooooooo',
  ],
  K: [
    '.g.....gg.....g.',
    '.gg...gggg...gg.',
    '.ggg.gggggg.ggg.',
    '.gggggggggggggg.',
    '.ggcggggggggcgg.',
    '.gggggggggggggg.',
    '.oooooooooooooo.',
    '..osssssssssso..',
    '..osesssssseso..',
    '..osssskssssso..',
    '..obbbbbbbbbbo..',
    '..obbbmmmmbbbo..',
    '..obbbbbbbbbbo..',
    '...obbbbbbbbo...',
    '....obbbbbbo....',
    '..ooooobboooooo.',
    '.occcccbbcccccco',
    'occcgcccccccgcco',
    'occcgcccccccgcco',
    'occcgggggggggcco',
    'occcccccccccccco',
    'oooooooooooooooo',
  ],
};

// Mockup PORTRAIT_COLOURS with the cream.css values filled in. 'c' is
// the suit ink, chosen per card at draw time.
export const PORTRAIT_COLOURS = {
  o: INK,
  s: '#f3c9a0',
  k: '#d9a074',
  h: '#5a3423',
  g: GOLD,
  c: null,
  w: '#fffaf0',
  e: INK,
  m: '#a8322c',
  b: '#f1ead8',
};

// ---- Pip layouts (mockup LAYOUTS): [column 0-2, row fraction 0-1] ----

export const LAYOUTS = {
  9: [[0, 0], [2, 0], [0, 1 / 3], [2, 1 / 3], [1, 0.5], [0, 2 / 3], [2, 2 / 3], [0, 1], [2, 1]],
  10: [[0, 0], [2, 0], [1, 1 / 6], [0, 1 / 3], [2, 1 / 3], [0, 2 / 3], [2, 2 / 3], [1, 5 / 6], [0, 1], [2, 1]],
};

// ---- Card geometry per size (mockup GEO), whole CSS px from the card's
// outer edge. `top` is the top edge of the highest pip row, `bottom` the
// top edge of the lowest; `cols` the left edges of the three pip columns;
// `frame` the portrait frame [x, y, w, h]; `ace` the ace's cell size.
export const GEO = {
  XL: { w: 128, h: 172, b: 3, suitPx: 2, pip: 2, cols: [22, 55, 88], top: 44, bottom: 140, ace: 6, portraitPx: 5, frame: [14, 42, 100, 122], step: 80 },
  L: { w: 115, h: 155, b: 3, suitPx: 2, pip: 2, cols: [20, 48, 76], top: 40, bottom: 126, ace: 5, portraitPx: 4, frame: [13, 40, 89, 109], step: 72 },
  M: { w: 104, h: 140, b: 3, suitPx: 2, pip: 2, cols: [18, 43, 68], top: 40, bottom: 112, ace: 5, portraitPx: 4, frame: [12, 40, 80, 96], step: 60 },
  S: { w: 92, h: 124, b: 3, suitPx: 2, pip: 2, cols: [16, 37, 58], top: 31, bottom: 98, ace: 4, portraitPx: 3, frame: [11, 30, 70, 88], step: 48 },
  XS: { w: 44, h: 60, b: 2, suitPx: 2, small: true },
  XXS: { w: 32, h: 44, b: 2, suitPx: 2, small: true },
};

// Corner index per size, from the mockup's style.css (.card .corner and
// .card .corner.mini): the corner box sits at (b + left, b + top); the
// rank is Tiny5 at `font` px with line height `line`; the suit follows
// `gap` px to the right (full cards) or below (small cards).
// Full cards: suit margin-top 6 px at 32 px type, 4 px at 24 px.
export const CORNER = {
  XL: { left: 3, top: 1, font: 32, line: 36, gap: 2, suitTop: 6 },
  L: { left: 3, top: 1, font: 32, line: 36, gap: 2, suitTop: 6 },
  M: { left: 3, top: 1, font: 32, line: 36, gap: 2, suitTop: 6 },
  S: { left: 3, top: 0, font: 24, line: 28, gap: 2, suitTop: 4 },
  XS: { left: 2, top: 1, font: 24, line: 28, gap: 2 },
  XXS: { left: 3, top: 1, font: 16, line: 18, gap: 1 },
};

// ---- Rank glyphs, Tiny5 at its native 8 px size ----
//
// How these were made: a headless Chromium page (deviceScaleFactor 1)
// set each rank (9, 10, J, Q, K, A) in client/public/fonts/Tiny5.ttf at
// 8, 16, 24 and 32 px, with the mockup's line heights (10, 18, 28, 36).
// A screenshot of each span was read pixel by pixel (dark = '#'). Every
// size came out with no grey pixels, and the 16, 24 and 32 px maps were
// exact 2x, 3x and 4x copies of the 8 px maps below. So one font pixel is
// font-size / 8 CSS px, the ink starts 2 font pixels below the top of the
// line box and at the left edge of the text box, and `advance` is the
// text box width in font pixels (the gap before the corner suit starts
// after it). Drawing these maps with fillRect gives the same pixels as
// the mockup's DOM text, without canvas text blur.
export const RANK_GLYPHS = {
  9: ['.#.', '#.#', '.##', '..#', '##.'],
  T: ['.#...#.', '##..#.#', '.#..#.#', '.#..#.#', '.#...#.'],
  J: ['..#', '..#', '..#', '#.#', '.#.'],
  Q: ['.##.', '#..#', '#..#', '#..#', '.##.', '...#'],
  K: ['#..#', '#.#.', '##..', '#.#.', '#..#'],
  A: ['.##.', '#..#', '####', '#..#', '#..#'],
};
export const RANK_ADVANCE = { 9: 4, T: 8, J: 4, Q: 5, K: 5, A: 5 };
export const RANK_INK_TOP = 2; // font pixels from line-box top to ink

/**
 * Count filled (non-'.') cells in a map. Pure, used by tests to pin the art.
 *
 * @param {string[]} map
 * @returns {number}
 */
export function countFilled(map) {
  let n = 0;
  for (const row of map) {
    for (const ch of row) if (ch !== '.') n += 1;
  }
  return n;
}

/**
 * Flood fill from every border '.' cell; returns true when no empty cell
 * is enclosed.
 *
 * @param {string[]} map
 * @returns {boolean} true when every '.' is reachable from the outside
 */
export function mapHasNoEnclosedHole(map) {
  const rows = map.length;
  const cols = map[0].length;
  const seen = Array.from({ length: rows }, () => new Array(cols).fill(false));
  /** @type {Array<[number, number]>} */
  const stack = [];
  for (let c = 0; c < cols; c += 1) {
    if (map[0][c] === '.') stack.push([0, c]);
    if (map[rows - 1][c] === '.') stack.push([rows - 1, c]);
  }
  for (let r = 0; r < rows; r += 1) {
    if (map[r][0] === '.') stack.push([r, 0]);
    if (map[r][cols - 1] === '.') stack.push([r, cols - 1]);
  }
  while (stack.length > 0) {
    const next = stack.pop();
    if (!next) continue;
    const [r, c] = next;
    if (r < 0 || c < 0 || r >= rows || c >= cols) continue;
    if (seen[r][c] || map[r][c] !== '.') continue;
    seen[r][c] = true;
    stack.push([r + 1, c], [r - 1, c], [r, c + 1], [r, c - 1]);
  }
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      if (map[r][c] === '.' && !seen[r][c]) return false;
    }
  }
  return true;
}

/**
 * Cell-size rule: the largest whole backing-store pixel cell that fits
 * `cells` art columns into `cssAvailPx` CSS px at the given DPR.
 *
 * The game canvas has no DPR transform (backing px == CSS px), so whole
 * CSS px cells map to whole device pixels at integer ratios 1, 2 and 3.
 * At a fractional ratio (1.25, 1.5) the browser upscales whole backing
 * pixels, so neighbouring art cells can differ by one device pixel; the
 * art stays aligned, just not uniformly sized. That is the cost noted in
 * the spec, and the reason art is integer-snapped rather than fractional.
 *
 * @param {number} cssAvailPx
 * @param {number} cells
 * @param {number} [dpr=1]
 * @returns {number} cell size in CSS px (whole backing pixels at dpr 1)
 */
export function pixelCell(cssAvailPx, cells, dpr = 1) {
  if (!Number.isFinite(cssAvailPx) || cssAvailPx <= 0) return 1;
  if (!Number.isFinite(cells) || cells <= 0) return 1;
  const ratio = Number.isFinite(dpr) && dpr > 0 ? dpr : 1;
  const devCells = Math.max(1, Math.floor((cssAvailPx * ratio) / cells));
  return Math.max(1, devCells / ratio);
}

/**
 * @typedef {{
 *   name: string, small: boolean, w: number, h: number,
 *   b: number, r: number, edge: number, trumpW: number,
 *   cornerX: number, cornerY: number, fc: number, sc: number,
 *   gap: number, suitTop: number, line: number,
 *   pip?: number, cols?: number[], top?: number, bottom?: number,
 *   ace?: number, portraitPx?: number, frame?: number[],
 * }} CardGeometry
 */

const FULL_BASES = ['XL', 'L', 'M', 'S'];

// Rank line height per rank cell (Tiny5 at 8, 16, 24 and 32 px; see
// RANK_GLYPHS for how these were measured).
const RANK_LINE = { 1: 10, 2: 18, 3: 28, 4: 36 };

// In-between small cards try these [suit cell, rank cell] pairs, biggest
// first. Each keeps the 5-row rank within one cell of the 9-row suit
// (15 px vs 18 px, 10 px vs 9 px), as the mockup's XS corner does.
const SMALL_CORNERS = [[2, 3], [1, 2]];

/**
 * Rank cell for a corner whose suit uses `sc` px cells: the value nearest
 * `preferred` whose 5-row rank is within one cell (the larger of the two
 * cells) of the 9-row suit. sc 1 gives 2 (10 px vs 9 px), sc 2 gives 3
 * or 4 (15 or 20 px vs 18 px), sc 3 gives 5 or 6.
 *
 * @param {number} sc
 * @param {number} preferred
 * @returns {number}
 */
export function rankCellFor(sc, preferred) {
  const ok = (f) => Math.abs(5 * f - 9 * sc) <= Math.max(f, sc);
  let best = null;
  for (let f = 1; f <= 2 * sc + 2; f += 1) {
    if (!ok(f)) continue;
    if (best === null || Math.abs(f - preferred) < Math.abs(best - preferred)) best = f;
  }
  if (best === null) throw new Error(`rankCellFor: no rank cell fits suit cell ${sc}`);
  return best;
}

// Solid edge below the card: 4 px on M/L/XL/S, 2 px on XS/XXS (the
// mockup's box-shadow), and the same clamp for in-between sizes.
function edgeFor(h) {
  return Math.max(2, Math.min(4, Math.round(h * 0.035)));
}

/**
 * Card geometry for a card of w x h CSS px (whole numbers).
 *
 * At a mockup size (XL, L, M, S, XS, XXS) this is the mockup's geometry
 * exactly. At any other size it scales the nearest full mockup size and
 * snaps every position and every art cell to whole CSS px (whole backing
 * pixels, see pixelCell), shrinking a cell when the scaled art would
 * collide. Cards under 41 px wide or 63 px tall use the small layout:
 * rank and suit only, like the mockup's XS and XXS.
 *
 * @param {number} w
 * @param {number} h
 * @returns {CardGeometry}
 */
export function cardGeometry(w, h) {
  const exact = Object.keys(GEO).find((k) => GEO[k].w === w && GEO[k].h === h);
  const small = exact ? !!GEO[exact].small : (w < 41 || h < 63);
  const edge = edgeFor(h);

  if (small) {
    let name = 'tiny';
    if (exact) name = exact;
    else if (w >= 44 && h >= 60) name = 'XS';
    else if (w >= 32 && h >= 44) name = 'XXS';
    const b = 2;
    // The mockup's small radius is 6 px; tinier cards round less.
    const r = name === 'tiny' ? Math.min(6, Math.max(2, Math.round(w / 6))) : 6;
    if (exact) {
      const cn = CORNER[name];
      return {
        name, small, w, h, b, r, edge, trumpW: 1,
        cornerX: b + cn.left, cornerY: b + cn.top,
        fc: cn.font / 8, sc: GEO[name].suitPx, gap: cn.gap,
        suitTop: cn.line + cn.gap, line: cn.line,
      };
    }
    // In-between small sizes: the biggest corner whose rank and suit are
    // about the same height (see SMALL_CORNERS) that fits inside the
    // outline. Under XXS the corner sits 1 px inside the outline.
    const cn = name === 'tiny' ? { left: 1, top: 1, gap: 1 } : CORNER[name];
    const cornerX = b + cn.left;
    const cornerY = b + cn.top;
    for (const [sc, fc] of SMALL_CORNERS) {
      const line = RANK_LINE[fc];
      const suitTop = Math.min(line + cn.gap, h - b - cornerY - 9 * sc);
      const fitsDown = suitTop >= (RANK_INK_TOP + 6) * fc; // below the Q tail
      const fitsAcross = cornerX + 7 * fc <= w - b && cornerX + 9 * sc <= w - b; // 7 = the 10's width
      if (fitsDown && fitsAcross) {
        return { name, small, w, h, b, r, edge, trumpW: 1, cornerX, cornerY, fc, sc, gap: cn.gap, suitTop, line };
      }
    }
    // Nothing fits (only the 17 x 24 side fans, which the game draws face
    // down): 8 px type and a 1-cell suit, kept inside the outline.
    return {
      name, small, w, h, b, r, edge, trumpW: 1, cornerX, cornerY, fc: 1, sc: 1, gap: 1,
      suitTop: Math.min(RANK_LINE[1] + 1, h - b - 1 - 9 - cornerY), line: RANK_LINE[1],
    };
  }

  if (exact) {
    const g = GEO[exact];
    const cn = CORNER[exact];
    return {
      name: exact, small, w, h, b: g.b, r: 9, edge, trumpW: 2,
      cornerX: g.b + cn.left, cornerY: g.b + cn.top,
      fc: cn.font / 8, sc: g.suitPx, gap: cn.gap, suitTop: cn.suitTop, line: cn.line,
      pip: g.pip, cols: g.cols.slice(), top: g.top, bottom: g.bottom,
      ace: g.ace, portraitPx: g.portraitPx, frame: g.frame.slice(),
    };
  }

  // Scale the nearest full mockup size.
  let baseName = FULL_BASES[0];
  for (const k of FULL_BASES) {
    if (Math.abs(GEO[k].w - w) < Math.abs(GEO[baseName].w - w)) baseName = k;
  }
  const g = GEO[baseName];
  const cn = CORNER[baseName];
  const s = w / g.w;
  const sy = h / g.h;
  const b = Math.max(2, Math.min(3, Math.round(g.b * s)));
  const r = Math.max(4, Math.min(9, Math.round(9 * s)));
  const gap = s >= 0.75 ? 2 : 1;
  const cornerX = b + Math.round(cn.left * s);
  const cornerY = b + Math.round(cn.top * sy);
  // The scaled mockup rank cell, the starting point for rankCellFor.
  const fcScaled = Math.round((cn.font / 8) * s);

  // Pip centres scale; the cell shrinks until neighbours keep a 1 px gap.
  // The corner suit uses the pip cell (the mockup's corner suit is the
  // pip size) and the rank cell follows it (rankCellFor), so the corner
  // grows and shrinks with the pips.
  const half = (9 * g.pip) / 2;
  const colCentres = g.cols.map((c) => (c + half) * s);
  const topCentre0 = (g.top + half) * sy;
  const bottomCentre = (g.bottom + half) * sy;
  const colGap = colCentres[1] - colCentres[0];
  let pip = Math.max(1, Math.round(g.pip * s));
  while (pip > 1 && 9 * pip + 1 > colGap) pip -= 1;
  let sc;
  let fc;
  let suitTop;
  let cornerBottom;
  let topCentre;
  for (;;) {
    sc = pip;
    fc = rankCellFor(sc, fcScaled);
    suitTop = Math.max(0, 2 * fc - 2);
    // Lowest corner pixel: the Q's tail (6 rows) or the corner suit.
    cornerBottom = Math.max(cornerY + (RANK_INK_TOP + 6) * fc, cornerY + suitTop + 9 * sc);
    // Keep the top pip row clear of the corner index.
    topCentre = Math.max(topCentre0, cornerBottom + 1 + (9 * pip) / 2);
    if (pip === 1 || 9 * pip + 1 <= (bottomCentre - topCentre) / 3) break;
    pip -= 1;
  }
  const cols = colCentres.map((c) => Math.round(c - (9 * pip) / 2));
  const top = Math.round(topCentre - (9 * pip) / 2);
  const bottom = Math.max(top, Math.round(bottomCentre - (9 * pip) / 2));

  const ace = Math.max(1, Math.min(Math.round(g.ace * s), Math.floor((w - 2 * b - 8) / 9)));

  const [fx, fy, fw, fh] = g.frame;
  const frameY = Math.max(Math.round(fy * sy), cornerBottom + 1);
  const frame = [Math.round(fx * s), frameY, Math.round(fw * s), Math.round((fy + fh) * sy) - frameY];
  const portraitPx = Math.max(1, Math.min(
    Math.round(g.portraitPx * s),
    Math.floor((frame[2] - 4) / PORTRAIT_COLS),
    Math.floor((frame[3] - 4) / PORTRAIT_ROWS),
  ));
  // When the portrait cell had to snap down, shrink the frame around it
  // (2 cells each side and 1.5 cells above, as on the M card) and centre
  // it in the scaled frame box, so the figure never floats in a big frame.
  const fitW = 20 * portraitPx;
  const fitH = PORTRAIT_ROWS * portraitPx + Math.round(1.5 * portraitPx) + 2;
  if (fitW < frame[2]) {
    frame[0] += Math.round((frame[2] - fitW) / 2);
    frame[2] = fitW;
  }
  if (fitH < frame[3]) {
    frame[1] += Math.round((frame[3] - fitH) / 2);
    frame[3] = fitH;
  }

  return {
    name: `${baseName}~${w}x${h}`, small, w, h, b, r, edge, trumpW: 2,
    cornerX, cornerY, fc, sc, gap, suitTop, line: (cn.line / cn.font) * 8 * fc,
    pip, cols, top, bottom, ace, portraitPx, frame,
  };
}
